import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { RadioBoxComponent, RadioOption } from '@libs/front/components/radiobox/radiobox.component';
import { ButtonComponent } from '@libs/front/components/button/button.component';
import { InputComponent } from '@libs/front/components/input/input.component';
import { DeviceService } from 'src/app/services/device.service';
import { SiteNavigationService } from 'src/app/services/new_services/site-navigation.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { CsvHoleImportModalComponent } from 'src/app/components/csv-hole-import-modal/csv-hole-import-modal.component';

import { BlastHolesStateService } from './blast-holes-state.service';
import { BlastHolesMapComponent } from './shared/blast-holes-map/blast-holes-map.component';
import { HolesStateViewComponent } from './views/holes-state-view/holes-state-view.component';
import { HolesGeologyViewComponent } from './views/holes-geology-view/holes-geology-view.component';
import { HolesQualityViewComponent } from './views/holes-quality-view/holes-quality-view.component';
import { HolesDrillingViewComponent } from './views/holes-drilling-view/holes-drilling-view.component';
import { HolesDeliveryViewComponent } from './views/holes-delivery-view/holes-delivery-view.component';
import { HolesViewPlaceholderComponent } from './views/holes-view-placeholder/holes-view-placeholder.component';
import {
  DEFAULT_HOLES_VIEW,
  HOLES_VIEWS,
  HOLES_VIEWS_WITH_LEGEND,
  HOLES_VIEW_LABEL_KEYS,
  HolesView,
  isHolesView,
} from './blast-holes.types';

// ── Map | table splitter ──────────────────────────────────────────────────────
/** Share of the width (left after the legend and the gaps) given to the map by default: the Figma 2fr / 3fr split. */
const MAP_SHARE_DEFAULT = 0.4;
/** Minimum widths of the two panes, in rem (also handed to the grid as CSS variables). */
const MAP_MIN_REM   = 20;
const PANEL_MIN_REM = 24;
/** Share moved per arrow key press on the focused splitter. */
const SPLITTER_KEY_STEP = 0.02;
const MAP_SHARE_STORAGE_KEY = 'explore.holes-tab.map-share';

/**
 * Blast › Holes tab.
 * Merges the legacy design / loading / 3D / delivery / drilling tabs into one screen
 * with five radio views. The active view lives in the `view` query param so each
 * view is deep-linkable (`?tab=holes&view=state`).
 *
 * Design source : Figma rHPoiskmYrtff7ZBjMWreI, node 2544:670215
 */
@Component({
  selector:        'app-blast-holes',
  standalone:      true,
  templateUrl:     './blast-holes.component.html',
  styleUrls:       ['./blast-holes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers:       [BlastHolesStateService],
  imports: [
    FormsModule,
    TranslateModule,
    RadioBoxComponent,
    ButtonComponent,
    InputComponent,
    BlastHolesMapComponent,
    CsvHoleImportModalComponent,
    HolesStateViewComponent,
    HolesGeologyViewComponent,
    HolesQualityViewComponent,
    HolesDrillingViewComponent,
    HolesDeliveryViewComponent,
    HolesViewPlaceholderComponent,
  ],
})
export class BlastHolesComponent {
  readonly blastId = input.required<number>();

  protected readonly state  = inject(BlastHolesStateService);
  protected readonly device = inject(DeviceService);
  private readonly route     = inject(ActivatedRoute);
  private readonly router    = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly siteNav   = inject(SiteNavigationService);
  private readonly userService = inject(UtilisateurService);

  // ── Active view (query param) ─────────────────────────────────────────────

  private readonly queryView = toSignal(
    this.route.queryParamMap.pipe(map(params => params.get('view'))),
    { initialValue: null as string | null },
  );

  /**
   * Sticky: a blast change from the legacy tree navigates with `?blastId=` only and the host
   * re-adds `tab` / `view` a tick later. Keeping the last valid view meanwhile avoids destroying
   * the view component (and its legend selection) during that hop.
   */
  readonly view = linkedSignal<string | null, HolesView>({
    source:      this.queryView,
    computation: (requested, previous) =>
      isHolesView(requested) ? requested : previous?.value ?? DEFAULT_HOLES_VIEW,
  });

  readonly hasLegend = computed(() => HOLES_VIEWS_WITH_LEGEND.has(this.view()));

  // ── Map | table splitter ──────────────────────────────────────────────────

  protected readonly MAP_MIN_REM   = MAP_MIN_REM;
  protected readonly PANEL_MIN_REM = PANEL_MIN_REM;

  private readonly body    = viewChild.required<ElementRef<HTMLElement>>('body');
  private readonly mapHost = viewChild.required<BlastHolesMapComponent, ElementRef<HTMLElement>>('mapHost', { read: ElementRef });
  private readonly map     = viewChild.required<BlastHolesMapComponent>('mapHost');

  /** Share of the map + table width given to the map (see `--holes-map-share` in the SCSS). Remembered per browser. */
  readonly mapShare = signal(readStoredMapShare());
  protected readonly mapSharePercent = computed(() => Math.round(this.mapShare() * 100));
  protected readonly splitterActive  = signal(false);

  /** Geometry frozen at drag start: the map's left edge does not move while dragging. */
  private drag: { mapLeft: number; available: number; splitterWidth: number } | null = null;

  // ── Radio options (re-translated on language change) ──────────────────────

  private readonly lang = toSignal(
    this.translate.onLangChange.pipe(map(event => event.lang)),
    { initialValue: this.translate.currentLang },
  );

  protected readonly viewOptions = computed<RadioOption[]>(() => {
    this.lang();
    return HOLES_VIEWS.map(view => ({
      value: view,
      label: this.translate.instant(HOLES_VIEW_LABEL_KEYS[view]),
    }));
  });

  constructor() {
    effect(() => {
      const id = this.blastId();
      if (id) untracked(() => void this.state.load(id));
    });
  }

  // ── Handlers ──────────────────────────────────────────────────────────────

  protected onViewChange(view: string): void {
    if (!isHolesView(view) || view === this.view()) return;
    // The tab is asserted too: a host page may have left `tab` empty, and a view change must not fall back
    // to the first blast tab.
    void this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         { tab: 'holes', view },
      queryParamsHandling: 'merge',
    });
  }

  protected onSplitterDown(event: PointerEvent): void {
    if (event.button !== 0) return;
    const splitter  = event.currentTarget as HTMLElement;
    const bodyRect  = this.body().nativeElement.getBoundingClientRect();
    const mapRect   = this.mapHost().nativeElement.getBoundingClientRect();
    const splitterWidth = splitter.getBoundingClientRect().width;
    this.drag = { mapLeft: mapRect.left, available: bodyRect.right - mapRect.left - splitterWidth, splitterWidth };
    splitter.setPointerCapture(event.pointerId);
    this.splitterActive.set(true);
    event.preventDefault();
  }

  protected onSplitterMove(event: PointerEvent): void {
    if (!this.drag) return;
    const { mapLeft, available, splitterWidth } = this.drag;
    if (available <= 0) return;
    const mapWidth = event.clientX - splitterWidth / 2 - mapLeft;
    this.applyMapShare(mapWidth / available, available);
  }

  protected onSplitterUp(): void {
    if (!this.drag) return;
    this.drag = null;
    this.splitterActive.set(false);
    storeMapShare(this.mapShare());
  }

  protected onSplitterKey(event: KeyboardEvent): void {
    const direction = event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0;
    if (!direction) return;
    event.preventDefault();
    const bodyRect = this.body().nativeElement.getBoundingClientRect();
    const mapRect  = this.mapHost().nativeElement.getBoundingClientRect();
    const available = bodyRect.right - mapRect.left - remToPx(1);
    this.applyMapShare(this.mapShare() + direction * SPLITTER_KEY_STEP, available);
    storeMapShare(this.mapShare());
  }

  protected resetSplit(): void {
    this.mapShare.set(MAP_SHARE_DEFAULT);
    storeMapShare(MAP_SHARE_DEFAULT);
  }

  /** Clamps the share so neither pane goes under its minimum width (the map wins when both cannot fit). */
  private applyMapShare(share: number, availablePx: number): void {
    const minMap   = remToPx(MAP_MIN_REM);
    const maxMap   = Math.max(minMap, availablePx - remToPx(PANEL_MIN_REM));
    const mapWidth = Math.min(maxMap, Math.max(minMap, share * availablePx));
    this.mapShare.set(Math.round((mapWidth / availablePx) * 1000) / 1000);
  }

  protected onSearchChange(term: string): void {
    this.state.searchTerm.set(term ?? '');
  }

  /** Opens the site Planning tab, on Deliveries from the Delivery view and on Drilling otherwise. */
  protected onPlanning(): void {
    const siteId = this.siteNav.siteId();
    if (!siteId) return;
    this.router.navigate(['/site', siteId], {
      queryParams: { tab: 'planning', planning: this.view() === 'delivery' ? 'delivery' : 'drilling' },
    });
  }

  protected readonly importOpen = signal(false);

  /** Site EPSG handed to the import wizard as the default source EPSG; null when unknown. */
  protected readonly siteEpsg = computed<number | null>(() => {
    const epsg = Number(this.siteNav.site()?.epsg ?? this.userService.currentSite()?.epsg);
    return Number.isFinite(epsg) && epsg > 0 ? epsg : null;
  });

  /** Opens the hole import wizard (formerly in the 3D viewer toolbar). */
  protected onImportHoles(): void {
    if (!this.state.blastId()) return;
    this.importOpen.set(true);
  }

  /** The wizard closed (imported, cancelled or dismissed). */
  protected onImportClosed(): void {
    this.importOpen.set(false);
  }

  /** Holes imported or blast renamed by the wizard: reload the holes and the 3D viewer. */
  protected onHolesImported(): void {
    void this.state.reload();
    this.map().reload3d();
  }
}

// ── Splitter helpers ──────────────────────────────────────────────────────────

function remToPx(rem: number): number {
  return rem * (parseFloat(getComputedStyle(document.documentElement).fontSize) || 16);
}

function readStoredMapShare(): number {
  try {
    const share = parseFloat(localStorage.getItem(MAP_SHARE_STORAGE_KEY) ?? '');
    return share > 0.05 && share < 0.95 ? share : MAP_SHARE_DEFAULT;
  } catch {
    return MAP_SHARE_DEFAULT;
  }
}

function storeMapShare(share: number): void {
  try {
    localStorage.setItem(MAP_SHARE_STORAGE_KEY, String(share));
  } catch {
    // Private mode / storage disabled: the split simply is not remembered.
  }
}
